import { Button, notify } from '@affine/component';
import {
  SettingsField,
  SettingsNote,
} from '@affine/component/setting-components';
import { SettingsSelect } from '@affine/core/desktop/dialogs/setting/shared/ui/settings-select';
import { SettingsEntityFormDialog } from '@affine/core/desktop/dialogs/setting/workspace-setting/shared/settings-form-dialog';
import { useI18n } from '@affine/i18n';
import { InformationIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useMemo, useState } from 'react';

import {
  ConnectServiceDialog,
  useConnectionServices,
} from '../connections/connect-service-dialog';
import type { WorkspaceConnectionSummary } from '../connections/types';
import {
  AutomationConfigApiService,
  type AutomationOwnConnection,
  type ConfigReadResponse,
} from '../services/automation-config-api';
import * as styles from './automation-takeover-dialog.css';
import {
  ownConnectionsOf,
  type TakeoverOpenStep,
  takeoverPlan,
} from './automation-takeover-plan';

/** No connection chosen for a step yet. */
const NONE = '';

export interface AutomationTakeoverDialogProps {
  workspaceId: string;
  docId: string;
  blockId: string;
  read: ConfigReadResponse;
  onClose: () => void;
  /** The config as the server reads it after taking over. */
  onTakenOver: (read: ConfigReadResponse) => void;
}

/**
 * Taking over a switched-on automation of someone else. Every step whose
 * credential the person may not use is resolved first: by a connection of
 * their own of the same service, by a new connection, or by asking the
 * credential's administrators to share it. Taking over starts only then.
 */
export function AutomationTakeoverDialog({
  workspaceId,
  docId,
  blockId,
  read,
  onClose,
  onTakenOver,
}: AutomationTakeoverDialogProps) {
  const t = useI18n();
  const configApi = useService(AutomationConfigApiService);
  const catalog = useConnectionServices(workspaceId);
  const [own, setOwn] = useState<readonly AutomationOwnConnection[]>(
    read.ownConnections
  );
  const [chosen, setChosen] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const [requested, setRequested] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [connectingStep, setConnectingStep] = useState<TakeoverOpenStep | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const plan = useMemo(
    () => takeoverPlan(read.connections, chosen),
    [chosen, read.connections]
  );
  const name =
    read.runsAs?.name ?? t['com.streamlain.automation.takeover.someone']();

  const choose = (stepId: string, connectionId: string) =>
    setChosen(current => {
      const next = new Map(current);
      if (connectionId === NONE) next.delete(stepId);
      else next.set(stepId, connectionId);
      return next;
    });

  const askToShare = async (step: TakeoverOpenStep) => {
    try {
      await configApi.requestCredentialShare(
        blockId,
        workspaceId,
        docId,
        step.connectionId
      );
      setRequested(current => new Set(current).add(step.connectionId));
    } catch (error) {
      notify.error({
        title: t['com.streamlain.automation.takeover.request-failed'](),
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const connected = (
    step: TakeoverOpenStep,
    connection: WorkspaceConnectionSummary
  ) => {
    setOwn(current => [
      ...current,
      {
        connectionId: connection.id,
        label: connection.label,
        serviceDefinitionId: connection.serviceDefinitionId,
      },
    ]);
    choose(step.stepId, connection.id);
    setConnectingStep(null);
  };

  const takeOver = async () => {
    setSaving(true);
    try {
      onTakenOver(
        await configApi.takeOver(blockId, workspaceId, docId, plan.choices)
      );
    } catch (error) {
      notify.error({
        title: t['com.streamlain.automation.takeover.failed'](),
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const connectingService = connectingStep
    ? catalog.services.find(
        service => service.id === connectingStep.serviceDefinitionId
      )
    : undefined;

  return (
    <>
      <SettingsEntityFormDialog
        open={connectingStep === null}
        onOpenChange={open => {
          if (!open) onClose();
        }}
        title={t['com.streamlain.automation.takeover.title']({ name })}
        width={560}
        onSave={() => void takeOver()}
        saveLabel={t['com.streamlain.automation.takeover.confirm']()}
        savingLabel={t['com.streamlain.automation.takeover.taking']()}
        cancelLabel={t['Cancel']()}
        saveDisabled={!plan.complete || read.deniedCredentialCount > 0}
        saving={saving}
        contentTestId="automation-takeover-dialog"
        saveTestId="automation-takeover-confirm"
      >
        <SettingsNote icon={<InformationIcon />}>
          {t['com.streamlain.automation.takeover.intro']({ name })}
        </SettingsNote>
        {read.deniedCredentialCount > 0 ? (
          <SettingsNote
            tone="error"
            icon={<InformationIcon />}
            data-testid="automation-takeover-denied-credentials"
          >
            {t['com.streamlain.automation.takeover.denied-credentials']({
              count: String(read.deniedCredentialCount),
            })}
          </SettingsNote>
        ) : null}
        <ul className={styles.steps}>
          {plan.kept.map(entry => (
            <li
              key={`${entry.stepId}:${entry.connectionId}`}
              className={styles.step}
              data-testid="automation-takeover-kept"
            >
              <strong>{entry.label ?? entry.connectionId}</strong>
              <span className={styles.usable}>
                {t['com.streamlain.automation.takeover.usable']()}
              </span>
            </li>
          ))}
          {plan.open.map(step => {
            const options = ownConnectionsOf(own, step.serviceDefinitionId);
            const isRequested = requested.has(step.connectionId);
            return (
              <li
                key={step.stepId}
                className={styles.step}
                data-testid="automation-takeover-open"
              >
                <strong>{step.label ?? step.connectionId}</strong>
                <SettingsField
                  label={t['com.streamlain.automation.takeover.own']()}
                >
                  {({ labelId }) => (
                    <SettingsSelect
                      labelledBy={labelId}
                      testId="automation-takeover-own"
                      value={chosen.get(step.stepId) ?? NONE}
                      disabled={options.length === 0}
                      placeholder={t[
                        'com.streamlain.automation.takeover.own-placeholder'
                      ]()}
                      onChange={value => choose(step.stepId, value)}
                      options={options.map(option => ({
                        value: option.connectionId,
                        label: option.label,
                      }))}
                    />
                  )}
                </SettingsField>
                <div className={styles.actions}>
                  <Button
                    data-testid="automation-takeover-connect"
                    disabled={
                      !catalog.services.some(
                        service => service.id === step.serviceDefinitionId
                      )
                    }
                    onClick={() => setConnectingStep(step)}
                  >
                    {t['com.streamlain.automation.takeover.connect']()}
                  </Button>
                  <Button
                    data-testid="automation-takeover-request"
                    disabled={isRequested}
                    onClick={() => void askToShare(step)}
                  >
                    {isRequested
                      ? t['com.streamlain.automation.takeover.requested']()
                      : t['com.streamlain.automation.takeover.request']()}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </SettingsEntityFormDialog>
      {connectingStep && connectingService ? (
        <ConnectServiceDialog
          workspaceId={workspaceId}
          services={[connectingService]}
          onClose={() => setConnectingStep(null)}
          onConnected={connection => connected(connectingStep, connection)}
        />
      ) : null}
    </>
  );
}
